import type { StorageAdapter } from "./StorageAdapter.ts";

/**
 * In-memory {@link StorageAdapter} — a drop-in fallback for when
 * `localStorage` is unreachable (sandboxed iframe, blocked site-data,
 * private-mode quota of 0). The game keeps running; saves simply do not
 * persist past the tab. Never throws.
 *
 * Used by {@link createStorageAdapter} when {@link isLocalStorageAvailable}
 * reports the real backing store is unusable (BL-85).
 */
export class MemoryStorageAdapter implements StorageAdapter {
  private readonly store = new Map<string, string>();
  private readonly prefix: string;

  constructor(prefix: string = "aiw_") {
    this.prefix = prefix;
  }

  async save(key: string, data: string): Promise<void> {
    this.store.set(this.prefix + key, data);
  }

  async load(key: string): Promise<string | null> {
    return this.store.get(this.prefix + key) ?? null;
  }

  async remove(key: string): Promise<void> {
    this.store.delete(this.prefix + key);
  }

  async listKeys(prefix: string): Promise<string[]> {
    const fullPrefix = this.prefix + prefix;
    const keys: string[] = [];
    for (const key of this.store.keys()) {
      if (key.startsWith(fullPrefix)) {
        keys.push(key.slice(this.prefix.length));
      }
    }
    return keys;
  }
}
